import { useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  message,
} from 'antd';
import { orgApi, userApi, workspaceApi } from '../api';
import { useAsync } from './useAsync';
import type { OrgRole, UserDetail, UserRow, WorkspaceRole } from '../types';
import { ORG_ROLE_LABEL, WORKSPACE_ROLE_LABEL } from '../types';

/**
 * Move a user from one organization to another.
 *
 * The repair for an account provisioned into the wrong org. Until this existed
 * the only answer was Delete followed by Create user, which fails on precisely
 * the accounts that need it most: one that has been used at all is no longer
 * deletable.
 *
 * The modal has two faces, like the delete one and for the same reason — the
 * preflight (`movable`, `move_blockers`) comes from the detail endpoint, which
 * would cost ten count queries a row to compute for the whole table, so it is
 * fetched once here at the point someone has actually reached for the action.
 *
 * What it emphasises is subtraction. A move is easy to read as "and also this
 * other org", which it is not: the old org's workspaces, capability grants,
 * pending requests and the user's own sessions all go. Saying so before the
 * button is the difference between a repair and a surprise.
 */
export default function MoveUserOrgModal({
  row,
  onClose,
  onMoved,
}: {
  row: UserRow;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [orgId, setOrgId] = useState<number | undefined>();
  const [workspaceIds, setWorkspaceIds] = useState<number[]>([]);
  const [workspaceRole, setWorkspaceRole] = useState<WorkspaceRole>('workspace_viewer');
  // undefined means "keep the position they hold", which is a real choice and
  // the default one — not an absent value the form has to fill in.
  const [orgRole, setOrgRole] = useState<OrgRole | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const detail = useAsync<UserDetail | null>(() => userApi.get(row.id), [row.id]);
  const user = detail.data;
  const blocked = !!user && !user.movable;

  const orgs = useAsync(() => orgApi.options(), []);
  // Only the destination org's workspaces, and only once one is picked: the API
  // rejects a workspace from anywhere else, and offering them would invite it.
  const workspaces = useAsync(
    () =>
      orgId
        ? workspaceApi.list({ org_id: orgId, per_page: 100 })
        : Promise.resolve({ items: [], total: 0 }),
    [orgId],
  );

  const destination = (orgs.data ?? []).find((o) => o.id === orgId);

  async function onSubmit() {
    if (!orgId) return;
    setSubmitting(true);
    setRefusal(null);
    try {
      const result = await userApi.moveToOrg(row.id, {
        org_id: orgId,
        org_role: orgRole,
        workspace_ids: workspaceIds,
        workspace_role: workspaceRole,
      });
      message.success(`${result.email} moved to ${result.org_name}.`);
      // The counts are the part worth reading back: they are what the move took
      // away, and nothing else in the console will mention it afterwards.
      const removed = [
        result.memberships_removed &&
          `${result.memberships_removed} workspace membership${
            result.memberships_removed === 1 ? '' : 's'
          }`,
        result.capability_grants_revoked &&
          `${result.capability_grants_revoked} capability grant${
            result.capability_grants_revoked === 1 ? '' : 's'
          }`,
        result.capability_requests_withdrawn &&
          `${result.capability_requests_withdrawn} pending request${
            result.capability_requests_withdrawn === 1 ? '' : 's'
          }`,
        result.invitations_cancelled &&
          `${result.invitations_cancelled} pending invitation${
            result.invitations_cancelled === 1 ? '' : 's'
          }`,
      ].filter(Boolean);
      if (removed.length) {
        message.info(
          `${result.previous_org_name ?? 'Their old organization'}: ${removed.join(
            ', ',
          )} removed.`,
          8,
        );
      }
      if (result.sessions_revoked) {
        // Not "signed out": user_tokens holds refresh tokens, and the access
        // token is a stateless 30-minute JWT nothing here can reach. The org
        // change is live regardless — the backend re-reads the user row on
        // every request — so what this sentence has to convey is the re-login,
        // not the cutover.
        message.info(
          'Their session can no longer be renewed, so they will be asked to sign in again within half an hour.',
          6,
        );
      }
      onMoved();
    } catch (error) {
      // Rendered here rather than toasted (the call is silent): a refusal names
      // the records keyed to this user, which is a paragraph and belongs next
      // to the button that was just refused.
      setRefusal(error instanceof Error ? error.message : 'The user was not moved.');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      width={620}
      title={blocked ? 'This user cannot be moved' : 'Move to another organization'}
      onCancel={onClose}
      okText="Move user"
      okButtonProps={{ disabled: !user?.movable || !orgId, loading: submitting }}
      onOk={onSubmit}
      // Nothing to confirm when the answer is already no.
      footer={blocked ? <Button onClick={onClose}>Close</Button> : undefined}
      destroyOnClose
    >
      {detail.loading || !user ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="User">
              {user.first_name} {user.last_name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <span className="mono">{user.email}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Currently in">
              {user.org_name ?? <Tag color="orange">No org</Tag>}
            </Descriptions.Item>
          </Descriptions>

          {blocked ? (
            // Not a permission problem and not a bug: records are keyed to this
            // account by the legacy owner scoping, and moving it would read one
            // customer's inventory into another customer's queries.
            <Alert
              type="info"
              showIcon
              message="Records are keyed to this user, not to a workspace"
              description={
                <>
                  <div>
                    The client app still reads these by owner rather than by
                    workspace, so they would follow the account into the other
                    organization:
                  </div>
                  <ul style={{ margin: '8px 0 8px 18px', padding: 0 }}>
                    {(user.move_blockers ?? []).map((blocker) => (
                      <li key={blocker.kind}>
                        {blocker.label} &mdash; <strong>{blocker.count}</strong>
                      </li>
                    ))}
                  </ul>
                  <div>
                    Set this account to <strong>Deactivated</strong> and create a
                    new one in the destination organization instead. Their work
                    keeps its author, and nothing crosses between customers.
                  </div>
                </>
              }
            />
          ) : (
            <>
              <Form layout="vertical" requiredMark={false}>
                <Form.Item
                  label="Move to"
                  required
                  extra="Only organizations other than the one they are in today."
                >
                  <Select
                    autoFocus
                    placeholder="Select organization"
                    loading={orgs.loading}
                    value={orgId}
                    onChange={(value: number) => {
                      setOrgId(value);
                      setWorkspaceIds([]);
                    }}
                    options={(orgs.data ?? [])
                      .filter((o) => o.id !== user.org_id)
                      .map((o) => ({
                        value: o.id,
                        label: o.tenant_subdomain
                          ? `${o.name} (${o.tenant_subdomain})`
                          : o.name,
                      }))}
                  />
                </Form.Item>
                <Form.Item
                  label="Workspaces"
                  extra="Leave empty to add them to the destination organization's default workspace. They must belong to at least one, or they can see nothing at all."
                >
                  <Select
                    mode="multiple"
                    disabled={!orgId}
                    loading={workspaces.loading}
                    placeholder={orgId ? 'Default workspace' : 'Select an organization first'}
                    value={workspaceIds}
                    onChange={setWorkspaceIds}
                    options={(workspaces.data?.items ?? []).map((w) => ({
                      value: w.id,
                      label: `${w.name}${w.country ? ` (${w.country})` : ''}`,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  label="Workspace role"
                  extra="Applies in each workspace above. Defaults to the least a move can grant — the destination organization did not ask for this user."
                >
                  <Select<WorkspaceRole>
                    value={workspaceRole}
                    onChange={setWorkspaceRole}
                    options={(Object.keys(WORKSPACE_ROLE_LABEL) as WorkspaceRole[]).map(
                      (value) => ({ value, label: WORKSPACE_ROLE_LABEL[value] }),
                    )}
                  />
                </Form.Item>
                <Form.Item
                  label="Org role"
                  extra="Their position in the new organization. Keeping it is usually right: a move corrects which company they are in, not what they do there."
                >
                  <Select<OrgRole>
                    allowClear
                    value={orgRole}
                    onChange={(value) => setOrgRole(value)}
                    placeholder={`Keep ${
                      user.org_role
                        ? ORG_ROLE_LABEL[user.org_role as OrgRole] ?? user.org_role
                        : 'as is'
                    }`}
                    options={(Object.keys(ORG_ROLE_LABEL) as OrgRole[]).map((value) => ({
                      value,
                      label: ORG_ROLE_LABEL[value],
                    }))}
                  />
                </Form.Item>
              </Form>

              {/* The half of the move people do not picture. Listed as counts
                  from this user's own detail, so it says what *this* move costs
                  rather than what a move costs in general. */}
              <Alert
                type="warning"
                showIcon
                message="What this takes away"
                description={
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    <li>
                      Their {user.workspaces.length} membership
                      {user.workspaces.length === 1 ? '' : 's'} in{' '}
                      {user.org_name ?? 'their current organization'}
                      {user.workspaces.length > 0 && (
                        <>
                          {' '}
                          &mdash;{' '}
                          <span className="mono">
                            {user.workspaces.map((w) => w.workspace_slug).join(', ')}
                          </span>
                        </>
                      )}
                    </li>
                    <li>
                      Capability grants and pending access requests in those
                      workspaces
                    </li>
                    <li>Any invitations they have sent that nobody has accepted yet</li>
                    <li>
                      Their ability to stay signed in &mdash; the new
                      organization applies to their very next action either way,
                      and they are asked to sign in again within half an hour
                    </li>
                  </ul>
                }
              />

              {destination && destination.tenant_subdomain !== user.tenant_subdomain && (
                <Alert
                  type="info"
                  showIcon
                  message="They also change whitelabel"
                  description={
                    <>
                      This account signs in at{' '}
                      <span className="mono">
                        {destination.tenant_subdomain ?? 'the bare Seiki domain'}
                      </span>{' '}
                      from now on, not{' '}
                      <span className="mono">
                        {user.tenant_subdomain ?? 'the bare Seiki domain'}
                      </span>
                      . Tell them, or their bookmark stops working.
                    </>
                  }
                />
              )}

              {user.is_seiki_staff && (
                <Alert
                  type="warning"
                  showIcon
                  message="This is a Seiki staff account"
                  description="Staff elevation is not an org position and survives the move untouched — this account keeps hard-delete and KPI recalculation everywhere."
                />
              )}

              {refusal && (
                <Alert type="error" showIcon message="Not moved" description={refusal} />
              )}
            </>
          )}
        </Space>
      )}
    </Modal>
  );
}

